import { zodResolver } from "@hookform/resolvers/zod";
import { CardPreview, Drawer } from "@verde/compose-ui";
import {
  useBulkInvestmentFundOperations,
  useCreateFundOperation,
  useFetchInvestmentFunds,
  useUpdateFundOperation,
} from "@verde/entities";
import { DocumentIcon, DownloadIcon, PlusIcon, XIcon } from "@verde/icons";
import {
  Alert,
  Attachment,
  Button,
  DatePicker,
  FormControl,
  Input,
  InputMask,
  InputMoney,
  Select,
  Switch,
} from "@verde/ui-core";
import { dayjs, useAlert } from "@verde/utils";
import { useCallback, useEffect } from "react";
import { Controller, type FieldErrors, useForm } from "react-hook-form";
import {
  type BulkSchema,
  type Schema,
  type SingleSchema,
  schema,
} from "./schemas";
import type { OperationFormParams } from "./types";

export default function OperationForm({
  open,
  onClose,
  operation,
  fund,
}: OperationFormParams) {
  const alert = useAlert();
  const isEditing = !!operation?.id;

  const funds = useFetchInvestmentFunds();
  const create = useCreateFundOperation();
  const update = useUpdateFundOperation(operation?.id);
  const bulk = useBulkInvestmentFundOperations(fund?.id, {
    hideSuccessFeedback: true,
  });

  const {
    reset,
    watch,
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      mode: "bulk",
    },
  });

  const modeValue = watch("mode");
  const fileValue = watch("file");

  const bulkErrors = errors as FieldErrors<BulkSchema>;
  const singleErrors = errors as FieldErrors<SingleSchema>;

  const loading = create.isLoading || update.isLoading || bulk.isLoading;

  const handleCleanForm = useCallback(() => {
    reset({
      client_document_number: undefined,
      client_name: undefined,
      due_date: undefined,
      operation_id: undefined,
      operation_value: undefined,
    });
  }, [reset]);

  const handleBulkFeedback = useCallback(async () => {
    return alert.execute({
      kind: "success",
      title: "Operações em lote",
      hideConfirmButton: true,
      cancelText: "Fechar",
      description: (
        <div className="grid gap-4 text-base">
          <p>Seu arquivo foi cadastrado com sucesso!</p>
          <p>
            O cadastro de operações em lote está em andamento e pode levar
            alguns minutinhos para finalizar. Isso garante que todos os seus
            dados sejam processados com segurança e precisão.
          </p>
        </div>
      ),
    });
  }, [alert]);

  const onSubmit = useCallback(
    async (values: Schema) => {
      if (values.mode === "single") {
        const payload = {
          ...values,
          mode: undefined,
          due_date: dayjs(values.due_date).format("YYYY-MM-DD"),
          operation_id: values.operation_id || undefined,
        };

        if (isEditing) {
          await update.mutateAsync(payload);
        } else {
          await create.mutateAsync(payload);
        }
      }

      if (values.mode === "bulk") {
        await bulk.mutateAsync(values.file);
        await handleBulkFeedback();
      }

      handleCleanForm();
      onClose?.();
    },
    [
      create,
      onClose,
      handleCleanForm,
      update,
      bulk,
      isEditing,
      handleBulkFeedback,
    ],
  );

  useEffect(() => {
    if (isEditing) {
      reset({
        mode: "single",
        client_document_number: operation?.client.document_number,
        client_name: operation?.client.name,
        due_date: operation?.due_date,
        operation_id: operation?.operation_id,
        operation_value: operation?.operation_value,
        investment_fund_id: operation?.investment_fund?.id,
      });
    }
  }, [operation, isEditing, reset]);

  useEffect(() => {
    if (fund?.id) {
      setValue("investment_fund_id", fund?.id);
    }
  }, [fund, setValue]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="sm"
      header={{
        icon: <PlusIcon />,
        title: operation?.id ? "Atualizar operação" : "Nova operação",
      }}
      footer={{
        primary: {
          children: isEditing
            ? "Atualizar operação"
            : modeValue === "bulk"
              ? "Cadastrar operações"
              : "Cadastrar operação",
          type: "submit",
          loading,
        },
        secondary: { children: "Fechar", onClick: onClose },
      }}
      context={<form onSubmit={handleSubmit(onSubmit)} />}
    >
      <div className="grid gap-6">
        {!fund?.id && (
          <FormControl
            id="investment_fund_id"
            label="Fundo"
            kind={errors.investment_fund_id ? "error" : "default"}
            helper={errors.investment_fund_id?.message}
          >
            <Controller
              control={control}
              name="investment_fund_id"
              render={({ field }) => (
                <Select
                  {...field}
                  options={funds.data?.data.map(({ id, name }) => ({
                    label: name,
                    value: id,
                  }))}
                  loading={funds.isLoading}
                />
              )}
            />
          </FormControl>
        )}

        {!isEditing && (
          <div className="grid gap-3">
            <Alert>
              Escolha como deseja cadastrar: em lote ou individualmente.
            </Alert>

            <Switch
              id="mode"
              label="Cadastrar operações em lote"
              layout="box"
              checked={modeValue === "bulk"}
              onChange={(event) => {
                setValue("mode", event.target.checked ? "bulk" : "single");
              }}
            />
          </div>
        )}

        <div className="flex flex-col rounded bg-neutral-light-1 p-4">
          {modeValue === "bulk" && (
            <div className="grid gap-4">
              <Button
                size="lg"
                kind="secondary"
                icon={<DownloadIcon />}
                onClick={() => {
                  window.open(
                    "https://docs.google.com/spreadsheets/d/1MzdWnlnC5_tWDnhL-T2knH4brPvR7Xi276jmA5QHDZs/export",
                    "_blank",
                  );
                }}
              >
                Baixar template
              </Button>

              {fileValue ? (
                <CardPreview
                  icon={<DocumentIcon />}
                  title="Lista de operações"
                  description={fileValue?.name}
                  actions={{
                    primary: {
                      icon: <XIcon />,
                      onClick: () => {
                        // @ts-expect-error
                        setValue("file", undefined);
                      },
                    },
                  }}
                />
              ) : (
                <Attachment
                  label="Lista de operações"
                  kind={bulkErrors.file ? "error" : "default"}
                  errorMessage={bulkErrors.file?.message}
                  accept={{
                    "text/csv": [".csv"],
                    "application/vnd.ms-excel": [".xls"],
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                      [".xlsx"],
                  }}
                  onChange={(event) => {
                    const file = event.target.files?.[0];

                    if (file) {
                      setValue("file", file);
                    }
                  }}
                />
              )}
            </div>
          )}

          {modeValue === "single" && (
            <div className="grid grid-cols-2 gap-x-2 gap-y-4">
              <FormControl
                id="client_document_number"
                label="CPF/CNPJ"
                helper={singleErrors.client_document_number?.message}
                kind={singleErrors.client_document_number ? "error" : "default"}
              >
                <Controller
                  control={control}
                  name="client_document_number"
                  render={({ field }) => (
                    <InputMask.Root
                      {...field}
                      options={InputMask.Options.Document}
                    />
                  )}
                />
              </FormControl>

              <FormControl
                id="client_name"
                condition="opcional"
                label="Nome completo"
                helper={singleErrors.client_name?.message}
                kind={singleErrors.client_name ? "error" : "default"}
              >
                <Input {...register("client_name")} />
              </FormControl>
              <FormControl
                id="due_date"
                label="Data vencimento"
                helper={singleErrors.due_date?.message}
                kind={singleErrors.due_date ? "error" : "default"}
              >
                <Controller
                  name="due_date"
                  control={control}
                  render={({ field }) => <DatePicker {...field} />}
                />
              </FormControl>

              <FormControl
                id="operation_value"
                label="Valor vencimento"
                helper={singleErrors.operation_value?.message}
                kind={singleErrors.operation_value ? "error" : "default"}
              >
                <Controller
                  control={control}
                  name="operation_value"
                  render={({ field }) => <InputMoney {...field} />}
                />
              </FormControl>

              <FormControl
                id="operation_id"
                label="ID Interno"
                helper={singleErrors.operation_id?.message}
                kind={singleErrors.operation_id ? "error" : "default"}
                condition="opcional"
              >
                <Input {...register("operation_id")} />
              </FormControl>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
